import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// Хук для дебаунсинга
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Хук для троттлинга
export const useThrottle = (value, delay) => {
  const [throttledValue, setThrottledValue] = useState(value);
  const lastExecuted = useRef(Date.now());

  useEffect(() => {
    if (Date.now() >= lastExecuted.current + delay) {
      lastExecuted.current = Date.now();
      setThrottledValue(value);
    } else {
      const timer = setTimeout(() => {
        lastExecuted.current = Date.now();
        setThrottledValue(value);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [value, delay]);

  return throttledValue;
};

// Хук для Intersection Observer
export const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);
  const elementRef = useRef();

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
        if (entry.isIntersecting && !hasIntersected) {
          setHasIntersected(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
        ...options
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [options, hasIntersected]);

  return [elementRef, isIntersecting, hasIntersected];
};

// Хук для видимости страницы
export const usePageVisibility = () => {
  const [isVisible, setIsVisible] = useState(!document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
};

// Хук для измерения производительности
export const usePerformanceMetrics = () => {
  const [metrics, setMetrics] = useState({
    loadTime: 0,
    renderTime: 0,
    memoryUsage: 0,
    fps: 0
  });

  useEffect(() => {
    // Измерение времени загрузки
    const loadTime = performance.now();
    setMetrics(prev => ({ ...prev, loadTime }));

    // Измерение использования памяти
    if ('memory' in performance) {
      const memoryInfo = performance.memory;
      setMetrics(prev => ({
        ...prev,
        memoryUsage: memoryInfo.usedJSHeapSize / 1024 / 1024 // MB
      }));
    }

    // Измерение FPS
    let frameCount = 0;
    let lastTime = performance.now();

    const measureFPS = () => {
      frameCount++;
      const currentTime = performance.now();
      
      if (currentTime - lastTime >= 1000) {
        setMetrics(prev => ({ ...prev, fps: frameCount }));
        frameCount = 0;
        lastTime = currentTime;
      }
      
      requestAnimationFrame(measureFPS);
    };

    requestAnimationFrame(measureFPS);

    return () => {
      // Cleanup
    };
  }, []);

  return metrics;
};

// Хук для ленивой загрузки изображений
export const useLazyImage = (src, options = {}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [error, setError] = useState(null);
  const imgRef = useRef();

  const { threshold = 0.1, rootMargin = '50px' } = options;

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(img);

    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  useEffect(() => {
    if (!isInView || !src) return;

    const img = new Image();
    
    img.onload = () => {
      setIsLoaded(true);
      setError(null);
    };
    
    img.onerror = () => {
      setError('Failed to load image');
    };
    
    img.src = src;
  }, [isInView, src]);

  return { imgRef, isLoaded, isInView, error };
};

// Хук для виртуализации
export const useVirtualization = (items, itemHeight, containerHeight) => {
  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef();

  const visibleStart = Math.floor(scrollTop / itemHeight);
  const visibleEnd = Math.min(
    visibleStart + Math.ceil(containerHeight / itemHeight),
    items.length
  );

  const visibleItems = items.slice(visibleStart, visibleEnd);
  const offsetY = visibleStart * itemHeight;
  const totalHeight = items.length * itemHeight;

  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  return {
    containerRef,
    visibleItems,
    offsetY,
    totalHeight,
    handleScroll,
    visibleStart,
    visibleEnd
  };
};

// Хук для предзагрузки ресурсов
export const usePreload = (resources) => {
  const [loadedResources, setLoadedResources] = useState(new Set());
  const [loadingResources, setLoadingResources] = useState(new Set());

  const preloadResource = useCallback(async (resource) => {
    if (loadedResources.has(resource.url) || loadingResources.has(resource.url)) {
      return;
    }

    setLoadingResources(prev => new Set([...prev, resource.url]));

    try {
      if (resource.type === 'image') {
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = resource.url;
        });
      } else if (resource.type === 'script') {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.onload = resolve;
          script.onerror = reject;
          script.src = resource.url;
          document.head.appendChild(script);
        });
      } else if (resource.type === 'style') {
        await new Promise((resolve, reject) => {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.onload = resolve;
          link.onerror = reject;
          link.href = resource.url;
          document.head.appendChild(link);
        });
      }

      setLoadedResources(prev => new Set([...prev, resource.url]));
    } catch (error) {
      console.error('Failed to preload resource:', resource.url, error);
    } finally {
      setLoadingResources(prev => {
        const newSet = new Set(prev);
        newSet.delete(resource.url);
        return newSet;
      });
    }
  }, [loadedResources, loadingResources]);

  useEffect(() => {
    resources.forEach(preloadResource);
  }, [resources, preloadResource]);

  return {
    loadedResources,
    loadingResources,
    preloadResource
  };
};

// Хук для оптимизации ре-рендеров
export const useStableCallback = (callback, deps) => {
  const callbackRef = useRef(callback);
  
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useCallback((...args) => {
    return callbackRef.current(...args);
  }, deps);
};

// Хук для мемоизации тяжелых вычислений
export const useHeavyComputation = (computeFn, deps, options = {}) => {
  const { debounceMs = 0 } = options;
  const [result, setResult] = useState(null);
  const [isComputing, setIsComputing] = useState(false);

  const debouncedDeps = useDebounce(deps, debounceMs);

  useEffect(() => {
    setIsComputing(true);
    
    // Используем setTimeout для неблокирующего выполнения
    const timer = setTimeout(() => {
      try {
        const computedResult = computeFn(debouncedDeps);
        setResult(computedResult);
      } catch (error) {
        console.error('Heavy computation error:', error);
        setResult(null);
      } finally {
        setIsComputing(false);
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [debouncedDeps, computeFn]);

  return { result, isComputing };
};

// Хук для оптимизации списков
export const useListOptimization = (items, options = {}) => {
  const {
    pageSize = 20,
    virtualScrolling = false,
    itemHeight = 50
  } = options;

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');

  // Фильтрация и сортировка
  const processedItems = useMemo(() => {
    let filtered = items;

    if (searchQuery) {
      filtered = filtered.filter(item =>
        JSON.stringify(item).toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a[sortBy];
        const bVal = b[sortBy];
        
        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }

    return filtered;
  }, [items, searchQuery, sortBy, sortOrder]);

  // Пагинация
  const paginatedItems = useMemo(() => {
    if (virtualScrolling) {
      return processedItems;
    }

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return processedItems.slice(startIndex, endIndex);
  }, [processedItems, currentPage, pageSize, virtualScrolling]);

  const totalPages = Math.ceil(processedItems.length / pageSize);

  return {
    items: paginatedItems,
    totalItems: processedItems.length,
    currentPage,
    totalPages,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    setCurrentPage,
    hasNextPage: currentPage < totalPages,
    hasPrevPage: currentPage > 1
  };
};
